import styled from 'styled-components';
import { Divider, Skeleton, Typography, message } from 'antd';
import CopyText from '../../../../assets/img/CopyMaskIcon.png';
import { ReactComponent as DownAngle } from '../../../../assets/img/icons/DownAngle.svg';
import VerticalDivider from 'assets/svgs/VerticalDivider';
import { useState, useEffect } from 'react';
import { RootState } from 'store';
import { useSelector } from 'react-redux';
import { IActionData } from '../interfaces';
import { getFormDetailsAction } from '../actions';
import { getQueryFormId } from 'modules/EmailBrowser/utils';
import CopyToClipboard from 'react-copy-to-clipboard';
import { FORM_HANDLER_USER_GUIDE } from '../constants';
import { Link } from 'react-router-dom';

const { Paragraph } = Typography;
const commonCSS = {
  margin: 0,
  fontFamily: 'Roboto',
  fontWeight: 500,
  lineHeight: '20px',
};
const fieldsCommonCSS = {
  display: 'flex',
  flex: 1,
  alignItems: 'center',
};

const SummaryCardWrapper = styled.div`
  box-shadow: 0px 0px 13px -2px #00000015;
  background: #fff;
  border: 1px solid transparent;
  border-radius: 5px;
  position: relative;
  padding: 13px 16px;
  @media all and (max-width: 1280px) and (-webkit-device-pixel-ratio: 1) {
    .field {
      flex-wrap: wrap;
    }
    .copy-field {
      width: calc(100% - 183px) !important;
    }
  }
  @media all and (max-width: 1024px) and (-webkit-device-pixel-ratio: 1.25) {
    .copy-field {
      width: calc(100% - 160px) !important;
    }
    .ant-typography {
      width: calc(42vw) !important;
    }
  }
  @media all and (max-width: 1280px) {
    .summary-text {
      width: 21vw !important;
    }
    .form-submission-summary-text {
      width: 100% !important;
    }
    @media all and (max-width: 1024px) and (-webkit-device-pixel-ratio: 1.25) {
      .summary-text {
        width: 16vw !important;
      }
      .copy-url-skeleton {
        width: 50vw !important;
      }
    }
  }
  .ant-typography {
    margin: 0;
    color: #7e7e7e;
    font-size: 16px;
    ${commonCSS}
  }
`;
const DateTimeField = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const Fields = styled.div`
  ${fieldsCommonCSS};
  margin-bottom: 24px;
`;
const SubmissionField = styled.div`
  display: flex;
  justify-content: space-between;
  ${fieldsCommonCSS}
`;
const CopyFields = styled.div`
  ${fieldsCommonCSS}
`;
const Label = styled.p`
  font-size: 18px;
  min-width: 183px;
  max-width: 183px;
  color: #5e7079;
  @media all and (max-width: 1200px) {
    min-width: 141px;
    max-width: 141px;
  }
  ${commonCSS}
`;
const SubmissionLabel = styled.p`
  font-size: 18px;
  min-width: 197px;
  max-width: 208px;
  color: #424b50;
  ${commonCSS}
`;
const ValueText = styled.p<{ disabled?: boolean }>`
  font-size: 16px;
  color: #7e7e7e;
  opacity: ${({ disabled }) => (disabled ? '50%' : '100%')};
  text-align: ${({ disabled }) => (disabled ? 'center' : 'left')};
  ${commonCSS};
  .list-name-link{
    color: #7e7e7e;
    :hover{
      color: #1EC252;
    }
  }
`;

const CopyField = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  word-break: break-all;
  border: 1px solid #1ec252;
  background: #def6e6;
  padding: 9px 0;
  border-radius: 5px;
  height: 38px;
`;
const Text = styled.p`
  margin: 0;
  font-size: 14px;
  font-family: 'Roboto';
  font-weight: 400;
  font-style: italic;
  line-height: 20px;
  color: #7e7e7e;
  margin-top: 8px;
  a {
    text-decoration: underline;
    font-size: 14px;
  }
  @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
    a {
      font-size: 13px !important;
    }
  }
`;

const SummarySection: React.FC = () => {
  const {
    name: formName,
    campaignName,
    createdAt,
    lastUpdated,
    createdBy,
    updatedBy,
    formUrl,
    actions: formSubmissionActions,
    fields: formFieldMapping,
  } = useSelector((state: RootState) => state.forms.selectedForm.data);
  const userFormsloading = useSelector((state: RootState) => state.forms.selectedForm.loading);

  const [openSubmissionCard, setOpenSubmissionCard] = useState(false);
  const [openMappingCard, setOpenMappingCard] = useState(false);

  const formFieldMappingKeys = Object.keys(formFieldMapping);
  const arrayLengthFormActions = formSubmissionActions.length;
  const arrayLengthFormMapping = formFieldMappingKeys.length;
  const formId = getQueryFormId();

  // Function to map form action data to dropdown
  const mapFormAction = (index: number, formAction: IActionData) => {
    let row = '';
    const listNames = String(formAction.list_names);
    const actionName = Object.keys(formAction)[0];
    const listIds = Object.values(formAction)[0];
    const listNameArray = listNames?.split(',');
    const listIdsArray = listIds?.toString()?.split(',');
    if (actionName) {
      row = index + `- ${actionName}: `;
    }
    return (
      <>
        {row}
        <span>
          {listNameArray?.map((name: string, index: number) => {
            return (<><Link className='list-name-link' to={`/audiences/view?list_id=${listIdsArray[index]}&form_id=${formId}`}>{name}</Link>{index !== listNameArray?.length - 1 && ', '}</>)
          })}
        </span>
      </>
    )
  };

  const checkEmptyAction = (formActions: IActionData[]) => {
    return formActions.length === 0 || formActions.some((fa) => fa.atl === null);
  };

  useEffect(() => {
    getFormDetailsAction(parseInt(formId));
  }, []);

  return (
    <>
      <SummaryCardWrapper>
        <Fields className="field">
          <Label className="summary-label">Name:</Label>
          {/* <ValueText className="summary-text"> */}
          <Paragraph className="summary-text" style={{ width: '85%' }} ellipsis={{ tooltip: formName }}>
            {userFormsloading ? <Skeleton.Input style={{ width: '100px', height: '16px', borderRadius: '8px' }} /> : formName}
          </Paragraph>
          {/* </ValueText> */}
        </Fields>
        <Fields className="field">
          <Label className="summary-label">Campaign:</Label>
          <Paragraph className="summary-text" style={{ width: '85%' }} ellipsis={{ tooltip: campaignName }}>
            {userFormsloading ? (
              <Skeleton.Input style={{ width: '100px', height: '16px', borderRadius: '8px' }} />
            ) : campaignName ? (
              campaignName
            ) : (
              'N/A'
            )}
          </Paragraph>
        </Fields>
        <DateTimeField>
          <Fields className="field">
            <Label className="summary-label">Created at:</Label>
            <Paragraph
              className="summary-text"
              style={{ width: '28vw' }}
              ellipsis={{ tooltip: `${createdAt} by ${createdBy}` }}
            >
              {userFormsloading ? <Skeleton.Input style={{ width: '100px', height: '16px', borderRadius: '8px' }} /> : `${createdAt} by ${createdBy}`}
            </Paragraph>
          </Fields>
          <Fields className="field">
            <Label className="summary-label">Updated at:</Label>
            <Paragraph
              className="summary-text"
              style={{ width: '28vw' }}
              ellipsis={{ tooltip: `${lastUpdated} by ${updatedBy}` }}
            >
              {userFormsloading ? (
                <Skeleton.Input style={{ width: '100px', height: '16px', borderRadius: '8px' }} />
              ) : (
                `${lastUpdated} by ${updatedBy}`
              )}
            </Paragraph>
          </Fields>
        </DateTimeField>
        <CopyFields className="field">
          <Label className="summary-label-url">End Point URL:</Label>
          {formUrl ? (
            <CopyField className="copy-field">
              <div style={{ padding: '0 16px' }}>
                <Paragraph
                  style={{ width: 'calc(100vw - 49vw)' }}
                  ellipsis={{ tooltip: { formUrl } }}
                >
                  {formUrl}
                </Paragraph>
              </div>
              <div
                onClick={() => message.success({ content: 'URL Copied', key: 1 })}
                style={{ display: 'flex', alignItems: 'center', marginLeft: 'auto', marginRight: '5px', cursor: 'pointer' }}
              >
                <CopyToClipboard text={formUrl}>
                  <img src={CopyText} alt="" />
                </CopyToClipboard>
              </div>
            </CopyField>
          ) : (
            <Skeleton.Input className="copy-url-skeleton" style={{ width: '60vw', height: '38px', borderRadius: '8px' }} />
          )}
        </CopyFields>
        <Fields className="field">
          <Label className="summary-label"></Label>
          {userFormsloading ? (
            <Skeleton.Input style={{ width: '450px', height: '20px', borderRadius: '8px', marginTop: '8px' }} />
          ) : (
            <Text>
              Copy and paste the Endpoint URL into the external form's code.{' '}
              <a href={FORM_HANDLER_USER_GUIDE} target="_blank">
                view guide
              </a>
            </Text>
          )}
        </Fields>
      </SummaryCardWrapper>
      <SummaryCardWrapper className="submission-field-wapper" onClick={() => setOpenSubmissionCard(!openSubmissionCard)}>
        <SubmissionField>
          <SubmissionLabel className="submission-label">Form Submission Actions</SubmissionLabel>
          <ValueText className="submission-text">
            <VerticalDivider />
            <DownAngle style={{ marginLeft: '9px', cursor: 'pointer' }} />
          </ValueText>
        </SubmissionField>
      </SummaryCardWrapper>
      {openSubmissionCard && (
        <SummaryCardWrapper className="form-submission-card">
          {checkEmptyAction(formSubmissionActions) ? (
            <>
              <ValueText key={1} disabled className="form-submission-summary-text">
                No Actions were selected
              </ValueText>
            </>
          ) : (
            formSubmissionActions.map((formAction, index) => (
              <>
                <ValueText key={index} className="form-submission-summary-text">
                  {mapFormAction(index + 1, formAction)}
                </ValueText>
                {index < arrayLengthFormActions - 1 && <Divider style={{ margin: '16px 0' }} />}
              </>
            ))
          )}
        </SummaryCardWrapper>
      )}
      <SummaryCardWrapper className="submission-field-wapper" onClick={() => setOpenMappingCard(!openMappingCard)}>
        <SubmissionField>
          <SubmissionLabel className="submission-label">Form Fields Mapping</SubmissionLabel>
          <ValueText className="submission-text">
            <VerticalDivider />
            <DownAngle style={{ marginLeft: '9px', cursor: 'pointer' }} />
          </ValueText>
        </SubmissionField>
      </SummaryCardWrapper>
      {openMappingCard && (
        <SummaryCardWrapper className="form-submission-card">
          {formFieldMappingKeys.map((formFieldKey, index) => (
            <>
              <ValueText key={index} className="form-mapping-summary-text">
                {index + 1}-External Field: "{formFieldMapping[parseInt(formFieldKey)].external_field}" will map to Internal Field: "
                {formFieldMapping[parseInt(formFieldKey)].internal_field}
                ".
                {/* {formFieldMapping[parseInt(formFieldKey)].is_required ? <RequiredText> (Required)</RequiredText> : ''} */}
              </ValueText>
              {index < arrayLengthFormMapping - 1 && <Divider style={{ margin: '16px 0' }} />}
            </>
          ))}
        </SummaryCardWrapper>
      )}
    </>
  );
};

export default SummarySection;
